import React from 'react'
import { useProcessEvent } from '@thesoulfresh/react-tools'

import { env } from '~/env'
import { Auth0 } from './Auth0'
import { AuthResponse, AuthService } from './AuthService'
import { makeLogger } from '~/utils'
import { AnalyticsEvents, AnalyticsService } from '../analytics'

const logger = makeLogger('useAuthService', '#37cda4', env.verbose)

// TODO Consider replacing this with Auth0's new hook:
// https://github.com/auth0/react-native-auth0?tab=readme-ov-file

/**
 * This hook stores the user's authenticated state and provides
 * login, logout and forgot login callbacks. Internally it uses
 * an auth service to perform the authentication steps.
 *
 * @param {*} [authService] - overrides the default auth service instance.
 */
export function useAuthService(
  authService: AuthService,
  trackEvent: AnalyticsService['track'],
  resetAnalytics: AnalyticsService['reset'],
  debug = /*istanbul ignore next*/ env.verbose,
) {
  const handleEvent = useProcessEvent()

  // Either use the auth service provided (for testing)
  // or generate one that gets reused between calls.
  const [service] = React.useState<AuthService>(
    () =>
      authService ||
      /*istanbul ignore next*/ new Auth0(undefined, undefined, debug),
  )

  const [startup, setStartup] = React.useState(true)
  const [initialized, setInitialized] = React.useState(false)
  const [authResponse, setAuthResponse] = React.useState<AuthResponse>()

  const [loading, _setLoading] = React.useState(true)

  // Duplicate the loading state using a Ref so we don't
  // need to regenerate the public callbacks whenever the
  // loading state changes.
  const internalLoadingState = React.useRef(loading)
  const setLoading = (l: boolean) => {
    internalLoadingState.current = l
    _setLoading(l)
  }

  // Handle the response data from a login request.
  const handleLogin = handleEvent((response: AuthResponse) => {
    if (response) {
      setAuthResponse(response)
      // We have been authenticated at least once now.
      setInitialized(true)
      setLoading(false)
    } else {
      setAuthResponse(null)
      setLoading(false)
    }
  })

  // Handle the response data from a logout request.
  const handleLogout = handleEvent(() => {
    // Clear the auth data.
    setAuthResponse(null)
    // Ensure that the initialized state is reset so that
    // no state is leaked to the next user.
    setInitialized(false)
  })

  const onLogout = React.useCallback(async () => {
    if (!internalLoadingState.current) {
      // Show the login screen immediately.
      handleLogout()
      setLoading(true)

      trackEvent(AnalyticsEvents.logout_start.name, {
        ...AnalyticsEvents.logout_start.build(),
        // Since these will be called outside of the Router context, they are
        // not available on native.
        title: 'unknown',
        path: 'unknown',
        search: 'unknown',
      })

      try {
        await service.logout()
        trackEvent(AnalyticsEvents.logout_success.name, {
          ...AnalyticsEvents.logout_success.build(),
          // Since these will be called outside of the Router context, they are
          // not available on native.
          title: 'unknown',
          path: 'unknown',
          search: 'unknown',
        })
      } catch (e) {
        /* istanbul ignore next: Logs are skipped during testing */
        if (debug) logger.debug('Logout request failed', e)
        trackEvent(AnalyticsEvents.logout_failure.name, {
          ...AnalyticsEvents.logout_failure.build(),
          // Since these will be called outside of the Router context, they are
          // not available on native.
          title: 'unknown',
          path: 'unknown',
          search: 'unknown',
        })
      }
      resetAnalytics()
      setLoading(false)
    } else {
      throw new Error(
        'Cannot call onLogout while another authentication state is loading.',
      )
    }
  }, [handleLogout, trackEvent, resetAnalytics, service, debug])

  const onLogin = React.useCallback(
    async (...args: Parameters<AuthService['login']>) => {
      if (!internalLoadingState.current) {
        setLoading(true)
        trackEvent(AnalyticsEvents.login_start.name, {
          ...AnalyticsEvents.login_start.build(),
          // Since these will be called outside of the Router context, they are
          // not available on native.
          title: 'unknown',
          path: 'unknown',
          search: 'unknown',
        })

        let response: AuthResponse
        try {
          // FYI, the loading state is not managed in this
          // function because it is dependent on the design of the login
          // page so it needs to be the Login page's responsibility.
          response = await service.login(...args)
          trackEvent(AnalyticsEvents.login_success.name, {
            ...AnalyticsEvents.login_success.build(),
            // Since these will be called outside of the Router context, they are
            // not available on native.
            title: 'unknown',
            path: 'unknown',
            search: 'unknown',
          })
        } catch (e) {
          // For now we'll catch the error here and continue normally
          // because that will turn off the loading indicator and will
          // show the login button again. If we decide to show custom
          // messages for certain errors, we'll need to allow the error
          // to bubble up further.
          /* istanbul ignore next: Logs are skipped during testing */
          if (debug) logger.log('Login failed', e)
          trackEvent(AnalyticsEvents.login_failure.name, {
            ...AnalyticsEvents.login_failure.build(),
            // Since these will be called outside of the Router context, they are
            // not available on native.
            title: 'unknown',
            path: 'unknown',
            search: 'unknown',
          })
        }

        handleLogin(response)
        return response
      } else {
        throw new Error(
          'Cannot call onLogin while another authentication state is loading.',
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service, debug],
  )

  // Lisen to login events
  React.useEffect(() => {
    service.addEventListener('login', handleLogin)
    return () => service.removeEventListener('login', handleLogin)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service])

  // Listen to logout events
  React.useEffect(() => {
    service.addEventListener('logout', handleLogout)
    return () => service.removeEventListener('logout', handleLogout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service])

  // This is similar to `onLogin` but should be used when
  // the user's session expires.
  // This function should NOT flip the initialized
  // flag because that will cause the main app bundle to be
  // reloaded.
  const onRefreshLogin = React.useCallback(
    async (...args: Parameters<AuthService['authenticate']>) => {
      /* istanbul ignore else: difficult to test */
      if (!internalLoadingState.current) {
        // Set the loading flag to indicate we're refreshing the login state.
        // Note: We leave the auth response object in place so the user data
        // continues to be displayed until we know for sure that the user needs
        // to reauthenticate.
        setLoading(true)

        let response: AuthResponse
        try {
          // Attempt to refresh the auth token without showing the login page.
          // If this fails, it's because our refresh token is expired at which
          // point we will want to clear the auth response object which will in
          // turn show the login page.
          response = await service.authenticate(...args)
        } catch (e) {
          /* istanbul ignore next: Logs are skipped during testing */
          logger.debug('onRefreshLogin service.authenticate() failed', e)
        }

        if (response) {
          setAuthResponse(response)
          setLoading(false)
        } else {
          // Clear the auth response which should result in the Login page being
          // displayed.
          setAuthResponse(null)
          setLoading(false)
          setInitialized(false)
        }

        return response
      } else {
        throw new Error(
          'Cannot call onRefreshLogin while another authentication state is loading.',
        )
      }
    },
    [service],
  )

  // Do whatever is necessary to get the user's
  // initial authenticated state.
  React.useEffect(() => {
    service
      .authenticate()
      .then((response) => {
        // Toggle the startup ref so we know the initial
        // auth state was determined. This is useful for
        // preventing animation of the login view when
        // the app first loads.
        setStartup(false)

        // If the user is authenticated, set the correct state
        // so the app removes the login window and shows the
        // main app.

        /* istanbul ignore next: hard to test */
        if (response) {
          setAuthResponse(response)
          setLoading(false)
          setInitialized(true)
        } else {
          // If the user is not authenticated, ensure that
          // the login page is shown and the loader is removed.
          // Allow the existing initialized state to persist
          // unless you specifically want to reset the
          // full application state.
          setAuthResponse(null)
          setLoading(false)
        }
      })
      .catch(() => {
        setAuthResponse(null)
        setStartup(false)
        setLoading(false)
        /* istanbul ignore next: Logs are skipped during testing */
        if (debug) logger.debug('User is logged out')
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Here we use the presence of auth response data
  // to indicate whether the user is currently authenticated
  // but this will vary based on your needs.
  const authenticated = !!authResponse

  // How you implement authentication is up to you but
  // the following API is expected.
  return {
    // Whether this render is the startup render where
    // we are determining the user's auth state for
    // the first time.
    startup: startup,
    // Whether the user has been authenticated at least
    // once during the session. The main app bundle will
    // not be loaded until this flag is set to true.
    //
    // IMPORTANT: This should be reset to false when the
    // user explicitly logs out. This way the application
    // state will be fully refreshed on the next login
    // ensuring that the first user's data is never exposed
    // to the next user.
    initialized,
    // Whether the user is currently authenticated. When
    // this flag is false, the login screen is shown.
    authenticated,
    // Whether the authentication service is currently working
    // to authenticate the user. When this flag is true,
    // the page loader is shown.
    loading,
    // This can be user data or whatever you get back from
    // your authentication service.
    authResponse,
    // A function to log the user out.
    onLogout,
    // A function to log the user in.
    onLogin,
    // A function to refresh the authenticated state of the user
    // without toggling the `initialized` flag. When using
    // the LoginOverlay component, using this function allows
    // you to maintain the application state during the re-authentication
    // process.
    onRefreshLogin,
  }
}
